/**
 * Layout «Espace de travail» — rediseño de la interfaz, opción 1a.
 *
 * Fuente: «Rediseño Galia - Opciones (1).pdf» (Drive de la AF, carpeta
 * «Nueva interfaz»). De las tres direcciones, Santiago eligió la primera en la
 * reunión del 15/09/2026, cambiando el ocre por el naranja de Galia #FE481B:
 *
 *   «Espace de travail — split fijo. Contenido y chat viven en dos columnas
 *    permanentes: nunca se tapan. Aire, jerarquía tipográfica y cero glass.
 *    La mascota vive discreta en la cabecera del chat.»
 *
 * Resuelve de paso tres hallazgos del informe UX de Melissa Amaya (11/09): el 5
 * (barra flotante sin jerarquía), el 9 (capas que se tapan) y el 10 (el panel
 * de configuración tapa la interfaz): en dos columnas fijas nada flota encima
 * del contenido ni del chat.
 *
 * Ajuste del 21/09/2026 (Santiago, tras probarlo en dev): la columna izquierda
 * NO abre Nomade de entrada — «es confuso» y no se veía el avatar. Ahora arranca
 * con una portada donde Galia (el avatar animado de siempre) pregunta qué se
 * quiere hacer; Nomade queda tras un botón (portada y cabecera) y se vuelve a
 * Galia con « Volver con Galia ».
 *
 * Es PURAMENTE de presentación: App.tsx le pasa como ranuras los mismos
 * elementos que ya arma (botones, micrófono, chat, visor). Toda la lógica
 * sigue en App, así que el layout clásico y este no pueden divergir en
 * comportamiento. Se activa con isWorkspaceLayoutEnabled() (solo dev).
 *
 * Textos de la interfaz: se sigue la convención actual de la app — español en
 * modo estudiante, francés en modo asistente. La maqueta está toda en francés;
 * si la Alianza quiere la interfaz del estudiante en francés, es una decisión
 * aparte (queda anotada para Santiago).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { MCERLevel, PracticeMode } from '../../types';
import { LottieAvatar } from '../Avatar/LottieAvatar';
import './WorkspaceLayout.css';
import './WorkspaceTheme.css';

interface WorkspaceLayoutProps {
  level?: MCERLevel;
  aideProf: boolean;
  /** Personal de la AF: ve el selector Estudiante / Profesor de la cabecera. */
  isTeacher: boolean;
  /** Cambia de modo desde el selector (true = modo asistente). */
  onSwitchMode?: (toAideProf: boolean) => void;
  /**
   * Aviso «ábrela en una pestaña nueva» cuando Galia va dentro de un iframe
   * (Apolearn). Va como franja propia sobre la cabecera: flotando la tapaba.
   */
  banner?: ReactNode;
  /** Botones de la cabecera (audio, configuración, nueva conversación…). */
  headerActions: ReactNode;
  /** Columna de contenido (visor Nomade / Cours internes en modo columna). */
  content: ReactNode;
  /**
   * El contenido es OPCIONAL (Santiago, 21/09): al entrar se ve a Galia, que
   * pregunta qué se quiere hacer; el visor solo se monta cuando el estudiante
   * lo pide (tarjeta de la portada o botón de la cabecera).
   */
  contentOpen: boolean;
  onOpenContent: () => void;
  onCloseContent: () => void;
  /**
   * Paisaje desbloqueable («la casa de Galia») para la portada, o null. En la
   * interfaz clásica era el fondo a pantalla completa; aquí es un cuadro con
   * Galia delante, a la vista mientras se conversa (Santiago, 21/09: no lo veía
   * porque este layout lo tenía apagado).
   */
  scene?: ReactNode;
  /** Modo de práctica: lo necesita el avatar animado de la portada. */
  mode?: PracticeMode;
  /** Nombre de pila para el saludo de la portada (si Apolearn lo dio). */
  firstName?: string;
  /** Chat embebido, con el micrófono en su campo de texto. */
  chat: ReactNode;
  /** Botón «Detener» mientras Galia habla (o null). */
  speakingControl?: ReactNode;
}

// Tipografías de la maqueta: Spectral (titulares editoriales) + Libre Franklin
// (interfaz). Se cargan SOLO cuando este layout está montado, para no sumar
// una petición de fuentes a la interfaz clásica ni a producción.
const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;500;600;700&family=Spectral:wght@500;600&display=swap';

// Exportado: la pantalla inicial también las usa cuando este layout está activo.
export function useWorkspaceFonts(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

// Los paneles que se abren FUERA del layout (configuración, información,
// valoración) se visten con el tema del espacio de trabajo mientras éste está
// montado: ver WorkspaceTheme.css.
function useWorkspaceThemeMarker() {
  useEffect(() => {
    document.documentElement.classList.add('ws-theme');
    return () => document.documentElement.classList.remove('ws-theme');
  }, []);
}

type MobileTab = 'chat' | 'content';

export function WorkspaceLayout({
  level,
  aideProf,
  isTeacher,
  onSwitchMode,
  banner,
  headerActions,
  content,
  contentOpen,
  onOpenContent,
  onCloseContent,
  scene,
  mode,
  firstName,
  chat,
  speakingControl,
}: WorkspaceLayoutProps) {
  useWorkspaceFonts();
  useWorkspaceThemeMarker();
  // En móvil las dos columnas se vuelven pestañas. Se entra por la de Galia
  // (portada con el paisaje; el visor cuando está abierto), como en escritorio.
  const [tab, setTab] = useState<MobileTab>('content');
  const chatRef = useRef<HTMLElement>(null);

  // En móvil, abrir el visor (desde la cabecera o la portada) lleva a su
  // pestaña; al cerrarlo la misma pestaña vuelve a mostrar la portada.
  useEffect(() => {
    if (contentOpen) setTab('content');
  }, [contentOpen]);

  // La tarjeta «¿Cómo fue tu experiencia?» vive FUERA del layout y en móvil
  // flota donde va el campo de texto del chat; en la pestaña de Galia no hay
  // campo y tapaba las tarjetas (Carlos, 22/09). Se marca la pestaña activa en
  // <html> y WorkspaceTheme.css la oculta fuera del chat (solo en móvil).
  useEffect(() => {
    document.documentElement.classList.toggle('ws-tab-content', tab === 'content');
    return () => document.documentElement.classList.remove('ws-tab-content');
  }, [tab]);

  // «Conversar con Galia»: lleva el cursor al campo de texto del chat.
  const focusChat = () => {
    setTab('chat');
    // Tras el cambio de pestaña en móvil el campo aún no es visible en este tick.
    setTimeout(() => {
      chatRef.current?.querySelector<HTMLTextAreaElement>('.chat-input')?.focus();
    }, 0);
  };

  const t = aideProf
    ? {
        student: 'Étudiant', teacher: 'Professeur', level: 'Niveau',
        online: 'En ligne · ton assistante', chat: 'Chat', content: 'Contenus', homeTab: 'Galia',
        hello: firstName ? `Bonjour, ${firstName} !` : 'Bonjour !',
        ask: "Qu'est-ce qu'on fait aujourd'hui ?",
        talkTitle: 'Parler avec Galia',
        talkDesc: 'Écris-lui ou utilise le micro : idées d’activités, exercices, évaluation…',
        contentTitle: 'Voir les contenus du cours',
        contentDesc: 'Nomade et Cours internes, pour préparer ta classe avec Galia.',
        back: 'Retour à Galia',
        contentBar: 'Contenus du cours',
      }
    : {
        student: 'Estudiante', teacher: 'Profesor', level: 'Nivel',
        online: 'En línea · tu compañera de francés', chat: 'Chat', content: 'Contenidos', homeTab: 'Galia',
        hello: firstName ? `¡Hola, ${firstName}! Soy Galia` : '¡Hola! Soy Galia',
        ask: '¿Qué quieres hacer hoy?',
        talkTitle: 'Conversar con Galia',
        talkDesc: 'Escríbele o háblale con el micrófono para practicar tu francés.',
        contentTitle: 'Ver los contenidos de Nomade',
        contentDesc: 'Abre los episodios de tu curso y coméntalos con Galia.',
        back: 'Volver con Galia',
        contentBar: 'Contenidos del curso',
      };

  return (
    <div className="ws-root">
      {banner && <div className="ws-banner">{banner}</div>}
      <header className="ws-header">
        <div className="ws-brand">
          <img src="/assets/ui/avatares/Galia_.png" alt="" className="ws-brand-logo" draggable={false} />
          <span className="ws-brand-name">Galia</span>
          <span className="ws-brand-sub">Alliance Française</span>
        </div>

        {isTeacher && onSwitchMode && (
          <div className="ws-role-toggle" role="group" aria-label={`${t.student} / ${t.teacher}`}>
            <button
              type="button"
              className={!aideProf ? 'is-active' : ''}
              aria-pressed={!aideProf}
              onClick={() => aideProf && onSwitchMode(false)}
            >
              {t.student}
            </button>
            <button
              type="button"
              className={aideProf ? 'is-active' : ''}
              aria-pressed={aideProf}
              onClick={() => !aideProf && onSwitchMode(true)}
            >
              {t.teacher}
            </button>
          </div>
        )}

        <div className="ws-header-right">
          {level && !aideProf && (
            <span className="ws-level-pill">
              {t.level} {level}
            </span>
          )}
          <div className="ws-header-actions">{headerActions}</div>
        </div>
      </header>

      <nav className="ws-mobile-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'content'}
          className={tab === 'content' ? 'is-active' : ''}
          onClick={() => setTab('content')}
        >
          {contentOpen ? t.content : t.homeTab}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'chat'}
          className={tab === 'chat' ? 'is-active' : ''}
          onClick={() => setTab('chat')}
        >
          {t.chat}
        </button>
      </nav>

      <main className="ws-body" data-mobile-tab={tab}>
        <section className="ws-content" aria-label={contentOpen ? t.content : 'Galia'}>
          {contentOpen ? (
            <>
              <div className="ws-content-bar">
                <button type="button" className="ws-content-back" onClick={onCloseContent}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
                  </svg>
                  <span>{t.back}</span>
                </button>
                <span className="ws-content-bar-title">{t.contentBar}</span>
              </div>
              {content}
            </>
          ) : (
            // Portada: Galia es lo primero que se ve y pregunta qué hacer. El
            // avatar es el mismo de la interfaz clásica, así que reacciona
            // cuando Galia habla, escucha o piensa.
            <div className={`ws-home ${scene ? 'has-scene' : ''}`}>
              {scene ? (
                <div className="ws-home-stage">
                  <div className="ws-home-stage-inner">
                    <div className="ws-scene-frame">{scene}</div>
                    <LottieAvatar mode={mode} avatarId="avatar1" />
                  </div>
                </div>
              ) : (
                <LottieAvatar mode={mode} avatarId="avatar1" />
              )}
              <h1 className="ws-home-hello">{t.hello}</h1>
              <p className="ws-home-ask">{t.ask}</p>
              <div className="ws-home-actions">
                <button type="button" className="ws-home-card is-primary" onClick={focusChat}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="ws-home-card-text">
                    <strong>{t.talkTitle}</strong>
                    <span>{t.talkDesc}</span>
                  </span>
                </button>
                <button type="button" className="ws-home-card" onClick={onOpenContent}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    <polygon points="11 7 15 9.5 11 12" fill="currentColor" stroke="none" />
                  </svg>
                  <span className="ws-home-card-text">
                    <strong>{t.contentTitle}</strong>
                    <span>{t.contentDesc}</span>
                  </span>
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="ws-chat" aria-label="Chat" ref={chatRef}>
          <div className="ws-chat-header">
            <img src="/assets/ui/avatares/Galia_.png" alt="" className="ws-chat-avatar" draggable={false} />
            <div className="ws-chat-title">
              <strong>Galia</strong>
              <span>
                <i className="ws-dot" aria-hidden="true" />
                {t.online}
              </span>
            </div>
            {speakingControl && <div className="ws-chat-speaking">{speakingControl}</div>}
          </div>
          {chat}
        </section>
      </main>
    </div>
  );
}

export default WorkspaceLayout;
